import React, { useEffect, useState, useRef, useImperativeHandle, useMemo, forwardRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import * as XLSX from 'xlsx';
import AntPopup from '../AntPopup';
import AntImportFileBrowser from './AntImportFileBrowser';
import AntImportFieldMapper from './AntImportFieldMapper';
import AntImportPreviewGrid from './AntImportPreviewGrid';
import AntConfig from '../../utils/AntConfig';
import AntCondition from '../../utils/AntCondition';
import AntLocalization from '../../utils/AntLocalization';
import AntTime from '../../utils/AntTime';
import AntAPI from '../../utils/AntAPI';
import { resolveImportClearField } from './AntImportRows';

import { AntGlobal, AntSession } from '../../utils/AntContext';

const basedate = new Date(1899, 11, 30, 0, 0, 0);
const dnthresh = basedate.getTime() + (new Date().getTimezoneOffset() - basedate.getTimezoneOffset()) * 60000;

const day_ms = 24 * 60 * 60 * 1000;
const days_1462_ms = 1462 * day_ms;

const datenum = (v, date1904) => {
    let epoch = v.getTime();
    if (date1904) {
        epoch -= days_1462_ms;
    }
    return (epoch - dnthresh) / day_ms;
};

const parseDDMMYYYY = (str) => {
    if (typeof str !== 'string') return null;

    const parts = str.split('/');
    if (parts.length !== 3) return null;

    let [d, m, y] = parts.map((p) => parseInt(p, 10));

    // validate đơn giản
    if (isNaN(d) || isNaN(m) || isNaN(y)) return null;
    if (m < 1 || m > 12 || d < 1 || d > 31) return null;

    return new Date(y, m - 1, d);
};
const parseDDMMYY = (str) => {
    if (typeof str !== 'string') return null;

    const parts = str.split('/');
    if (parts.length !== 3) return null;
    // Năm 4 chữ số để parseDDMMYYYY xử lý (tránh 2026 → 3926)
    if (parts[2].trim().length > 2) return null;

    let [d, m, y] = parts.map((p) => parseInt(p, 10));

    // validate đơn giản
    if (isNaN(d) || isNaN(m) || isNaN(y)) return null;
    if (m < 1 || m > 12 || d < 1 || d > 31) return null;

    // chuyển 2 chữ số năm thành 4 chữ số
    if (y < 50) {
        y = 2000 + y;
    } else {
        y = 1900 + y;
    }

    return new Date(y, m - 1, d);
};

const fixImportedDate = (date, isDate1904) => {
    if (typeof date === 'string') {
        let parsedStr = parseDDMMYY(date);
        if (!parsedStr) parsedStr = parseDDMMYYYY(date);
        if (!parsedStr) return date; // throw new Error('Invalid date string: ' + date);
        date = parsedStr;
    }
    try {
        const parsed = XLSX.SSF.parse_date_code(datenum(date, isDate1904), { date1904: isDate1904 });

        return new Date(parsed.y, parsed.m - 1, parsed.d, parsed.H, parsed.M, parsed.S);
    } catch (error) { }

    return date;
};

// Overlay khi AI đang phân tích — inline style để tránh phụ thuộc SCSS build.
function AntImportAIProcessing({ fileData, progressText }) {
    var name = (fileData && fileData.name) || AntLocalization.translateText({ vi: 'Tài liệu của bạn', en: 'Your document', fr: 'Votre document', es: 'Su documento', id: 'Dokumen Anda', ja: 'あなたの文書', zh: '您的文档', ko: '내 문서', hi: 'आपका दस्तावेज़', ar: 'مستندك' });
    var sizeKB = fileData && fileData.file && fileData.file.size ? (fileData.file.size / 1024).toFixed(1) + ' KB' : '';
    var lines = [
        { top: 14, right: 32 }, { top: 26 }, { top: 38, right: 22 }, { top: 50, right: 42 },
        { top: 62 }, { top: 74, right: 26 }, { top: 86, right: 38 }, { top: 98 }
    ];
    return (
        <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            minHeight: 400, flex: 1, padding: 24
        }}>
            <style>{`@keyframes ant-import-ai-scan { 0%,100%{top:0%;} 50%{top:78%;} }`}</style>
            <div style={{
                display: 'flex', gap: 28, alignItems: 'center',
                padding: '28px 36px',
                background: 'linear-gradient(135deg, #eff4ff 0%, #f7faff 60%, #fafbfc 100%)',
                border: '1px solid #d8e3f5', borderRadius: 14, maxWidth: 520,
                boxShadow: '0 12px 40px rgba(15, 23, 42, 0.08)'
            }}>
                {/* Doc thumbnail with 8 lines + scan animation */}
                <div style={{
                    width: 100, height: 130, background: '#fff',
                    border: '1px solid #e2e8f0', borderRadius: 6,
                    position: 'relative', flexShrink: 0,
                    boxShadow: '0 4px 14px rgba(15, 23, 42, 0.05)', overflow: 'hidden'
                }}>
                    {lines.map((l, idx) => (
                        <div key={idx} style={{
                            position: 'absolute', left: 12, right: l.right != null ? l.right : 12,
                            height: 3, background: '#e2e8f0', borderRadius: 2, top: l.top
                        }} />
                    ))}
                    <div style={{
                        position: 'absolute', left: 0, right: 0, height: 20, top: '30%',
                        background: 'linear-gradient(to bottom, transparent, rgba(37,99,235,0.22), transparent)',
                        borderTop: '1px solid rgba(37,99,235,0.7)',
                        animation: 'ant-import-ai-scan 2.2s ease-in-out infinite'
                    }} />
                </div>
                {/* Text block */}
                <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 18, fontWeight: 600, color: '#0f172a', letterSpacing: '-0.01em' }}>
                        {AntLocalization.translateText({ vi: 'AI đang phân tích…', en: 'AI is analyzing…', fr: 'L’IA analyse…', es: 'La IA está analizando…', ja: 'AI が解析中…', zh: 'AI 正在分析…', ko: 'AI 분석 중…', hi: 'AI विश्लेषण कर रहा है…', ar: 'الذكاء الاصطناعي يحلل…', id: 'AI menganalisis…' })}
                    </div>
                    <div style={{
                        marginTop: 6, fontSize: 13, color: '#64748b',
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
                    }}>
                        {name}{sizeKB ? ' · ' + sizeKB : ''}
                    </div>
                    <div style={{ marginTop: 12, fontSize: 13, color: '#1d4ed8' }}>
                        {progressText || AntLocalization.translateText({ vi: 'Vui lòng chờ trong giây lát', en: 'Please wait a moment', fr: 'Veuillez patienter un instant', es: 'Espere un momento', id: 'Mohon tunggu sebentar', ja: 'しばらくお待ちください', zh: '请稍候', ko: '잠시만 기다려 주세요', hi: 'कृपया थोड़ी प्रतीक्षा करें', ar: 'يرجى الانتظار لحظة' })}
                    </div>
                </div>
            </div>
        </div>
    );
}

function AIPreprocessorPanel({ state, aiAvailable, onToggleAI, onChangeHint, onRunAI, onReopenJob, onClearError, onOpenQueue, onDismissAsync }) {
    var fd = state.fileData;
    var isSpreadsheet = fd && fd.kind === 'spreadsheet';
    var isNonSpreadsheet = fd && fd.kind && fd.kind !== 'spreadsheet';
    var recentJobs = state.recentJobs || [];
    var quota = aiAvailable && aiAvailable.quota;
    var tier = aiAvailable && aiAvailable.tier;
    // Nút "Phân tích" hiện khi: đã chọn file + AI available + không processing + không async job đang chờ.
    // Với spreadsheet: cần user tick useAI thì mới hợp lệ (spreadsheet có thể đi mapping thủ công).
    // Với non-spreadsheet: hợp lệ ngay (chỉ có đường AI).
    var canRunAI = fd && fd.file && aiAvailable && !state.aiProcessing && !state.aiAsyncJob
        && (isNonSpreadsheet || (isSpreadsheet && state.useAI));

    var jobStatusBadge = (st) => {
        var map = {
            'Pending': { bg: '#f1f5f9', fg: '#475569', txt: { vi: 'Đang chờ', en: 'Pending', fr: 'En attente', es: 'Pendiente', id: 'Menunggu', ja: '待機中', zh: '等待中', ko: '대기 중', hi: 'लंबित', ar: 'معلّق' } },
            'Processing': { bg: '#dbeafe', fg: '#1d4ed8', txt: { vi: 'Đang xử lý', en: 'Processing', fr: 'Traitement', es: 'Procesando', id: 'Memproses', ja: '処理中', zh: '处理中', ko: '처리 중', hi: 'प्रसंस्करण', ar: 'جاري المعالجة' } },
            'AwaitingUser': { bg: '#fef3c7', fg: '#b45309', txt: { vi: 'Chờ review', en: 'Awaiting', fr: 'À vérifier', es: 'Por revisar', id: 'Perlu dicek', ja: '確認待ち', zh: '待审核', ko: '검토 대기', hi: 'समीक्षा शेष', ar: 'للمراجعة' } },
            'Applied': { bg: '#dcfce7', fg: '#15803d', txt: { vi: 'Đã import', en: 'Applied', fr: 'Importé', es: 'Importado', id: 'Diimpor', ja: '取込済み', zh: '已导入', ko: '가져옴', hi: 'आयातित', ar: 'تم الاستيراد' } },
            'Failed': { bg: '#fee2e2', fg: '#b91c1c', txt: { vi: 'Lỗi', en: 'Failed', fr: 'Échec', es: 'Error', id: 'Gagal', ja: '失敗', zh: '失败', ko: '실패', hi: 'विफल', ar: 'فشل' } }
        };
        var s = map[st] || { bg: '#f1f5f9', fg: '#475569', txt: { vi: st || '?', en: st || '?' } };
        return (
            <span style={{ background: s.bg, color: s.fg, padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 500 }}>
                {AntLocalization.translateText(s.txt)}
            </span>
        );
    };

    var formatTime = (val) => {
        if (!val) return '';
        try {
            var ms = typeof val === 'number' ? (val < 1e12 ? val * 1000 : val) : new Date(val).getTime();
            var diff = (Date.now() - ms) / 1000;
            if (diff < 60) return Math.round(diff) + 's ' + AntLocalization.translateText({ vi: 'trước', en: 'ago', fr: 'plus tôt', es: 'atrás', id: 'lalu', ja: '前', zh: '前', ko: '전', hi: 'पहले', ar: 'مضت' });
            if (diff < 3600) return Math.round(diff / 60) + 'm ' + AntLocalization.translateText({ vi: 'trước', en: 'ago', fr: 'plus tôt', es: 'atrás', id: 'lalu', ja: '前', zh: '前', ko: '전', hi: 'पहले', ar: 'مضت' });
            if (diff < 86400) return Math.round(diff / 3600) + 'h ' + AntLocalization.translateText({ vi: 'trước', en: 'ago', fr: 'plus tôt', es: 'atrás', id: 'lalu', ja: '前', zh: '前', ko: '전', hi: 'पहले', ar: 'مضت' });
            return Math.round(diff / 86400) + 'd ' + AntLocalization.translateText({ vi: 'trước', en: 'ago', fr: 'plus tôt', es: 'atrás', id: 'lalu', ja: '前', zh: '前', ko: '전', hi: 'पहले', ar: 'مضت' });
        } catch (e) { return ''; }
    };

    return (
        <div className="ant-import-ai-panel" style={{ marginTop: 8, padding: '10px 12px', background: '#f8fafc', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 13 }}>
            <style>{`@keyframes ant-import-ai-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
            {/* AI availability banner */}
            {aiAvailable && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                    <span style={{ color: '#0f766e', fontWeight: 600, fontSize: 12 }}>
                        {tier === 'onboarding'
                            ? AntLocalization.translateText({ vi: 'AI miễn phí', en: 'Free AI', fr: 'IA gratuite', es: 'IA gratis', id: 'AI gratis', ja: '無料AI', zh: '免费AI', ko: '무료 AI', hi: 'निःशुल्क AI', ar: 'AI مجاني' })
                            : tier === 'tier1'
                                ? AntLocalization.translateText({ vi: 'CORE-AI miễn phí', en: 'Free CORE-AI', fr: 'CORE-AI gratuit', es: 'CORE-AI gratis', id: 'CORE-AI gratis', ja: '無料CORE-AI', zh: '免费CORE-AI', ko: '무료 CORE-AI', hi: 'निःशुल्क CORE-AI', ar: 'CORE-AI مجاني' })
                                : tier === 'ai'
                                    ? AntLocalization.translateText({ vi: 'Gói ANTE-AI', en: 'ANTE-AI plan', fr: 'Forfait ANTE-AI', es: 'Plan ANTE-AI', id: 'Paket ANTE-AI', ja: 'ANTE-AIプラン', zh: 'ANTE-AI套餐', ko: 'ANTE-AI 플랜', hi: 'ANTE-AI प्लान', ar: 'خطة ANTE-AI' })
                                    : AntLocalization.translateText({ vi: 'AI dùng API key riêng', en: 'AI with your own API key', fr: 'IA avec votre clé API', es: 'IA con su clave API', id: 'AI dengan API key sendiri', ja: '自分のAPIキーでAI', zh: '使用自有API key的AI', ko: '자체 API 키 사용 AI', hi: 'अपनी API key वाला AI', ar: 'AI بمفتاح API خاص' })}
                    </span>
                    {tier === 'tier1' && quota && quota.remaining_daily != null && (
                        <span style={{ color: '#64748b', fontSize: 11 }}>
                            {AntLocalization.translateText({ vi: 'Quota:', en: 'Quota:', fr: 'Quota :', es: 'Cuota:', id: 'Kuota:', ja: '利用枠:', zh: '额度:', ko: '한도:', hi: 'कोटा:', ar: 'الحصة:' })} {quota.remaining_daily}/{(quota.remaining_daily + (quota.daily || 0))} {AntLocalization.translateText({ vi: 'hôm nay', en: 'today', fr: 'ce jour', es: 'hoy', id: 'hari ini', ja: '本日', zh: '今天', ko: '오늘', hi: 'आज', ar: 'اليوم' })}
                        </span>
                    )}
                </div>
            )}
            {!aiAvailable && (
                <div style={{ color: '#94a3b8', fontSize: 12, marginBottom: 6 }}>
                    {AntLocalization.translateText({ vi: 'AI hiện không khả dụng (hết quota hoặc chưa cấu hình)', en: 'AI not available (out of quota or not configured)', fr: 'IA indisponible (quota épuisé ou non configurée)', es: 'IA no disponible (sin cuota o sin configurar)', id: 'AI tidak tersedia (kuota habis atau belum dikonfigurasi)', ja: 'AIは利用できません（利用枠超過または未設定）', zh: 'AI暂不可用（额度已用完或未配置）', ko: 'AI를 사용할 수 없습니다 (한도 소진 또는 미설정)', hi: 'AI उपलब्ध नहीं (कोटा समाप्त या कॉन्फ़िगर नहीं)', ar: 'AI غير متاح (انتهت الحصة أو لم يُهيَّأ)' })}
                </div>
            )}

            {/* Toggle AI cho file Excel/CSV — rõ ràng, dễ thấy. Default OFF. */}
            {isSpreadsheet && aiAvailable && (
                <label
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 12,
                        cursor: 'pointer',
                        padding: '10px 14px',
                        marginTop: 6,
                        background: state.useAI ? '#eff6ff' : '#fff',
                        border: '1.5px solid ' + (state.useAI ? '#1d4ed8' : '#cbd5e1'),
                        borderRadius: 8,
                        transition: 'all 0.15s ease'
                    }}
                >
                    <input
                        type="checkbox"
                        checked={!!state.useAI}
                        onChange={(e) => onToggleAI(e.target.checked)}
                        style={{ width: 18, height: 18, cursor: 'pointer', accentColor: '#1d4ed8' }}
                    />
                    <div style={{ flex: 1 }}>
                        <div style={{ color: '#0f172a', fontWeight: 600, fontSize: 14 }}>
                            {AntLocalization.translateText({ vi: 'Dùng AI phân tích file', en: 'Analyze with AI', fr: 'Analyser avec IA', es: 'Analizar con IA', id: 'Analisis dengan AI', ja: 'AIで分析', zh: 'AI 分析', ko: 'AI로 분석', hi: 'AI से विश्लेषण', ar: 'تحليل بالذكاء' })}
                        </div>
                        <div style={{ color: '#64748b', fontSize: 12, marginTop: 2 }}>
                            {AntLocalization.translateText({ vi: 'AI tự bóc tách + đề xuất ánh xạ cột; bỏ qua nếu file đã đúng template', en: 'AI auto-extracts + suggests column mapping; skip if file follows the standard template', fr: 'L\'IA extrait + propose le mappage des colonnes ; inutile si le fichier suit le modèle', es: 'La IA extrae + sugiere el mapeo de columnas; omita si el archivo sigue la plantilla', id: 'AI mengekstrak + menyarankan pemetaan kolom; lewati jika file sudah sesuai templat', ja: 'AIが自動抽出し列マッピングを提案します。標準テンプレートのファイルなら不要です', zh: 'AI自动提取并建议列映射；文件已符合标准模板可跳过', ko: 'AI가 자동 추출하고 열 매핑을 제안합니다. 표준 템플릿 파일이면 건너뛰세요', hi: 'AI स्वतः डेटा निकालकर कॉलम मैपिंग सुझाता है; फ़ाइल मानक टेम्पलेट में हो तो छोड़ें', ar: 'AI يستخرج تلقائياً + يقترح ربط الأعمدة؛ تخطَّ إذا كان الملف وفق القالب' })}
                        </div>
                    </div>
                </label>
            )}
            {isNonSpreadsheet && aiAvailable && (
                <div style={{ marginTop: 4 }}>
                    <div style={{ color: '#475569', fontSize: 12, marginBottom: 4 }}>
                        {AntLocalization.translateText({ vi: 'Gợi ý nội dung file (không bắt buộc):', en: 'Content hint (optional):', fr: 'Indice sur le contenu (facultatif) :', es: 'Pista del contenido (opcional):', id: 'Petunjuk isi file (opsional):', ja: 'ファイル内容のヒント（任意）:', zh: '文件内容提示（可选）:', ko: '파일 내용 힌트 (선택):', hi: 'फ़ाइल सामग्री संकेत (वैकल्पिक):', ar: 'تلميح عن محتوى الملف (اختياري):' })}
                    </div>
                    <textarea
                        value={state.aiHint || ''}
                        onChange={(e) => onChangeHint(e.target.value)}
                        rows={2}
                        placeholder={AntLocalization.translateText({ vi: 'VD: menu nhà hàng, gồm món ăn và đồ uống', en: 'e.g. restaurant menu with foods and drinks', fr: 'Ex. : menu de restaurant, plats et boissons', es: 'Ej.: menú de restaurante con platos y bebidas', id: 'Mis.: menu restoran berisi makanan dan minuman', ja: '例: 料理とドリンクを含むレストランのメニュー', zh: '例如：餐厅菜单，含菜品和饮料', ko: '예: 음식과 음료가 포함된 레스토랑 메뉴', hi: 'उदा.: रेस्तराँ मेनू, व्यंजन और पेय सहित', ar: 'مثال: قائمة مطعم تشمل أطعمة ومشروبات' })}
                        style={{ width: '100%', border: '1px solid #cbd5e1', borderRadius: 6, padding: '6px 8px', fontSize: 13, fontFamily: 'inherit', resize: 'vertical' }}
                    />
                </div>
            )}

            {/* Nút "Phân tích" — trigger AI thủ công sau khi chọn file. */}
            {canRunAI && (
                <div style={{ marginTop: 8, display: 'flex', justifyContent: 'flex-end' }}>
                    <button
                        type="button"
                        onClick={onRunAI}
                        style={{ background: '#1d4ed8', color: '#fff', border: 'none', borderRadius: 6, padding: '7px 16px', fontSize: 13, fontWeight: 500, cursor: 'pointer' }}
                    >
                        <span className="dx-icon dx-icon-isnotblank" style={{ marginRight: 6, verticalAlign: '-2px' }} />
                        {AntLocalization.translateText({ vi: 'Phân tích bằng AI', en: 'Analyze with AI', fr: 'Analyser par IA', es: 'Analizar con IA', id: 'Analisis dengan AI', ja: 'AIで分析', zh: '用AI分析', ko: 'AI로 분석', hi: 'AI से विश्लेषण', ar: 'تحليل بـ AI' })}
                    </button>
                </div>
            )}

            {/* Khi aiProcessing → main AntPopup render s2-hero overlay full body,
                AIPreprocessorPanel hide. Không cần render gì ở đây. */}

            {/* Async banner — job đang chạy nền, cho user mở queue page */}
            {state.aiAsyncJob && !state.aiProcessing && (
                <div style={{ marginTop: 8, padding: '10px 12px', background: '#eff6ff', borderRadius: 6, color: '#1d4ed8', fontSize: 12, border: '1px solid #bfdbfe' }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                        <span className="dx-icon dx-icon-clock" style={{ fontSize: 16 }} />
                        <div style={{ flex: 1 }}>
                            <div style={{ fontWeight: 600, marginBottom: 2 }}>
                                {AntLocalization.translateText({ vi: 'AI đang xử lý nền', en: 'AI processing in background', fr: 'Traitement IA en arrière-plan', es: 'IA procesando en segundo plano', id: 'AI memproses di latar belakang', ja: 'AIがバックグラウンドで処理中', zh: 'AI正在后台处理', ko: 'AI가 백그라운드에서 처리 중', hi: 'AI पृष्ठभूमि में चल रहा है', ar: 'AI يعالج في الخلفية' })}
                            </div>
                            <div style={{ color: '#475569', fontSize: 11 }}>
                                {AntLocalization.translateText({ vi: 'File:', en: 'File:', fr: 'Fichier:', es: 'Archivo:', id: 'File:', ja: 'File:', zh: '文件:', ko: '파일:', hi: 'फ़ाइल:', ar: 'الملف:' })} <b>{state.aiAsyncJob.file_name || state.aiAsyncJob.code}</b>
                                {state.aiAsyncJob.code && <span> · {AntLocalization.translateText({ vi: 'Mã:', en: 'Code:', fr: 'Code :', es: 'Código:', id: 'Kode:', ja: 'コード:', zh: '编号:', ko: '코드:', hi: 'कोड:', ar: 'الرمز:' })} {state.aiAsyncJob.code}</span>}
                            </div>
                            <div style={{ color: '#64748b', fontSize: 11, marginTop: 2 }}>
                                {AntLocalization.translateText({ vi: 'Khi xong sẽ có thông báo. Mở hàng đợi AI để xem chi tiết tiến trình.', en: 'You will be notified when done. Open AI queue to track progress.', fr: 'Vous serez notifié à la fin. Ouvrez la file IA pour suivre la progression.', es: 'Se le notificará al terminar. Abra la cola de IA para seguir el progreso.', id: 'Anda akan diberi tahu saat selesai. Buka antrean AI untuk memantau progres.', ja: '完了時に通知します。AIキューで進捗を確認できます。', zh: '完成后会通知您。打开AI队列可查看进度。', ko: '완료되면 알림을 보냅니다. AI 대기열에서 진행 상황을 확인하세요.', hi: 'पूरा होने पर सूचना मिलेगी। प्रगति देखने के लिए AI कतार खोलें।', ar: 'سيصلك إشعار عند الانتهاء. افتح قائمة انتظار AI لمتابعة التقدم.' })}
                            </div>
                        </div>
                        <span style={{ cursor: 'pointer', color: '#1e40af', fontSize: 16, padding: '0 4px' }} onClick={onDismissAsync} title="Close">×</span>
                    </div>
                    <div style={{ marginTop: 8, display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                        <button
                            onClick={onOpenQueue}
                            style={{ background: '#1d4ed8', color: '#fff', border: 'none', borderRadius: 4, padding: '5px 12px', fontSize: 12, fontWeight: 500, cursor: 'pointer' }}
                        >
                            {AntLocalization.translateText({ vi: 'Xem hàng đợi AI', en: 'Open AI queue', fr: 'Ouvrir la file IA', es: 'Abrir cola de IA', id: 'Buka antrean AI', ja: 'AIキューを開く', zh: '打开AI队列', ko: 'AI 대기열 열기', hi: 'AI कतार खोलें', ar: 'فتح قائمة AI' })}
                        </button>
                    </div>
                </div>
            )}

            {/* Error */}
            {state.aiError && !state.aiProcessing && (
                <div style={{ marginTop: 8, padding: '8px 10px', background: '#fef2f2', borderRadius: 6, color: '#b91c1c', fontSize: 12, display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                    <span>{state.aiError}</span>
                    <span style={{ cursor: 'pointer', color: '#7f1d1d' }} onClick={onClearError}>×</span>
                </div>
            )}

            {/* Recent jobs */}
            {recentJobs.length > 0 && (
                <div style={{ marginTop: 10, borderTop: '1px solid #e2e8f0', paddingTop: 8 }}>
                    <div style={{ color: '#475569', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>
                        {AntLocalization.translateText({ vi: 'Lần import AI gần đây', en: 'Recent AI imports', fr: 'Imports IA récents', es: 'Importaciones IA recientes', id: 'Impor AI terbaru', ja: '最近のAIインポート', zh: '最近的AI导入', ko: '최근 AI 가져오기', hi: 'हाल के AI आयात', ar: 'عمليات استيراد AI الأخيرة' })} ({recentJobs.length})
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, maxHeight: 140, overflowY: 'auto' }}>
                        {recentJobs.map((j) => {
                            var canReopen = j.job_status === 'AwaitingUser';
                            return (
                                <div key={j.id || j.code} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 6px', borderRadius: 4, background: '#fff', border: '1px solid #e2e8f0' }}>
                                    <span style={{ flex: 1, color: '#0f172a', fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                        {j.file_name || j.code}
                                    </span>
                                    {jobStatusBadge(j.job_status)}
                                    <span style={{ color: '#94a3b8', fontSize: 11, minWidth: 64, textAlign: 'right' }}>
                                        {formatTime(j.created_at)}
                                    </span>
                                    {canReopen && (
                                        <button
                                            onClick={() => onReopenJob(j)}
                                            style={{ background: '#0ea5e9', color: '#fff', border: 'none', borderRadius: 4, padding: '2px 8px', fontSize: 11, cursor: 'pointer' }}
                                        >
                                            {AntLocalization.translateText({ vi: 'Mở lại', en: 'Reopen', fr: 'Rouvrir', es: 'Reabrir', id: 'Buka lagi', ja: '再表示', zh: '重新打开', ko: '다시 열기', hi: 'फिर खोलें', ar: 'إعادة فتح' })}
                                        </button>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
}

export default forwardRef(function (props, ref) {
    const appGlobal = AntGlobal();
    const session = AntSession();
    const navigate = useNavigate();

    const tableName = props.table ? props.table : props.API ? props.API.entityName : '';
    const entityKey = props.entity_key ? props.entity_key : 'id';
    const entityName = props.API && props.API.entityName ? props.API.entityName : '';
    const entityDisplay = props.API && props.API.entityDisplay ? props.API.entityDisplay : props.entityDisplay ? props.entityDisplay : '';

    // AI preprocessor settings (Spec §6.4)
    const aiTargetTable = props.aiTargetTable || tableName;
    // aiVariant: pass `null` explicit để KHÔNG kèm key `variant` trong input_data
    // (vd worker AIVoucherAnalysis tự infer theo target_table). undefined → default.
    const aiVariant = props.aiVariant !== undefined ? props.aiVariant : 'MasterData';
    const aiJobType = props.aiJobType || 'extract_master_data';
    // entity dùng cho rows-style (extract_master_data, extract_voucher_items). Worker
    // voucher-level (extract_voucher) không cần entity — truyền null explicit để bỏ key.
    // undefined → default 'product' (backward-compat cho AIMasterAnalysis flow).
    const aiEntity = props.aiEntity !== undefined ? props.aiEntity : 'product';
    // Bonus input_data theo job_type (vd intent, profile_code, country_code cho extract_voucher,
    // hoặc { deploy_id, deploy_code } cho AIMasterAnalysis customer flow).
    const aiExtraInputData = props.aiExtraInputData || null;
    const aiSyncThresholdMB = typeof props.aiSyncThresholdMB === 'number' ? props.aiSyncThresholdMB : 5;
    const aiAllowedKinds = props.aiAllowedFormats || ['spreadsheet', 'pdf', 'image'];

    const aiJobQueueAPI = useMemo(() => {
        try { return new AntAPI('ai', 'AIJobQueue'); } catch (e) { return null; }
    }, []);
    const config = useMemo(() => {
        let found = appGlobal.tableConfigs?.find((t) => t.tableName === tableName);
        if (found) return found.config;

        let cnf = new AntConfig(appGlobal, tableName);
        cnf.configFields(props.fields);

        if (appGlobal.tableConfigs instanceof Array === false) appGlobal.tableConfigs = [];

        appGlobal.tableConfigs.push({
            tableName: tableName,
            config: cnf
        });
        return cnf;
    }, []);

    const isCreatable =
        props.createable &&
        AntCondition.isValid(props.parentData, {
            when: props.createable.when,
            whenType: props.createable.whenType
        });

    const isUpdatable =
        props.updateable &&
        AntCondition.isValid(props.parentData, {
            when: props.updateable.when,
            whenType: props.updateable.whenType
        });

    const isCreateOnly = isUpdatable === false && isCreatable;
    const isUpdateOnly = isCreatable === false && isUpdatable;

    const title = isCreateOnly
        ? AntLocalization.translateText({ vi: 'Tạo', en: 'Create', fr: 'Créer', es: 'Crear', id: 'Buat', ja: '作成', zh: '创建', ko: '생성', hi: 'बनाएं', ar: 'إنشاء' })
        : isUpdateOnly
            ? AntLocalization.translateText({ vi: 'Cập nhật', en: 'Update', fr: 'Mettre à jour', es: 'Actualizar', id: 'Perbarui', ja: '更新', zh: '更新', ko: '업데이트', hi: 'अपडेट करें', ar: 'تحديث' })
            : AntLocalization.translateText({ vi: 'Xử lý', en: 'Process', fr: 'Traiter', es: 'Procesar', id: 'Proses', ja: '処理', zh: '处理', ko: '처리', hi: 'प्रक्रिया', ar: 'معالجة' });

    const show = async (obj) => {
        if (obj)
            Object.keys(obj).forEach((k) => {
                if (obj[k] === undefined) delete obj[k];
            });

        forceRenderWithState({
            defaultObj: obj ? obj : {},
            currentStage: 'FileBrowse',
            fileData: null,
            useAI: false,
            aiHint: '',
            aiAvailable: null,
            recentJobs: [],
            aiProcessing: false,
            aiProgressStep: 0,
            aiProgressText: '',
            aiError: '',
            aiJob: null,
            aiAsyncJob: null
        });

        if (popup.current) popup.current.show();

        // Async-load AI availability + recent jobs (non-blocking)
        if (aiEnabled && aiJobQueueAPI) {
            loadAIAvailability();
            loadRecentJobs();
        }
    };

    const aiEnabled = useMemo(() => {
        // opt-in: parent must explicitly pass aiEnabled=true (backward-compat default = false)
        if (props.aiEnabled !== true) return false;
        if (!aiJobType) return false;
        return true;
    }, [props.aiEnabled, aiJobType]);

    const loadAIAvailability = useCallback(async () => {
        if (!aiJobQueueAPI || !aiJobType) return;
        try {
            var result = await aiJobQueueAPI.postActionWithData('actionCheckQuota', { job_type: aiJobType });
            var data = (result && result.data) || result || {};
            if (data.error) {
                forceRenderWithState({ aiAvailable: null });
                return;
            }
            // Master data trong 48 giờ đầu dùng system AI miễn phí, không trừ
            // CORE-AI. Sau đó theo precedence BYO → ANTE-AI → CORE-AI.
            var available = null;
            if (data.onboardingFree) {
                available = { tier: 'onboarding', quota: data.quota };
            } else if (data.tier2Available) {
                available = { tier: 'tier2', quota: null };
            } else if (data.sources && data.sources.ai) {
                available = { tier: 'ai', quota: null };
            } else if (data.tier1AllowedForJob && data.quota && data.quota.ok) {
                available = { tier: 'tier1', quota: data.quota };
            }
            // Default useAI = false. User PHẢI tick checkbox cho spreadsheet
            // (Excel/CSV). Non-spreadsheet (PDF/ảnh) đã có nút "Phân tích" hiển
            // thị ngay không cần state.useAI (xem canRunAI).
            forceRenderWithState({
                aiAvailable: available,
                useAI: false
            });
        } catch (e) {
            forceRenderWithState({ aiAvailable: null });
        }
    }, [aiJobQueueAPI, aiJobType]);

    const loadRecentJobs = useCallback(async () => {
        if (!aiJobQueueAPI || !aiTargetTable) return;
        try {
            var result = await aiJobQueueAPI.postList({
                source_target_table: aiTargetTable,
                job_type: aiJobType,
                ordering: [{ name: 'created_at', type: 'desc' }],
                limit: 5,
                offset: 0
            });
            var rows = (result && result.rows) || [];
            forceRenderWithState({ recentJobs: rows });
        } catch (e) {
            forceRenderWithState({ recentJobs: [] });
        }
    }, [aiJobQueueAPI, aiTargetTable, aiJobType]);

    const uploadFileToMedia = async (file) => {
        if (!aiJobQueueAPI || !file) throw new Error('No AI API or file');
        return new Promise((resolve, reject) => {
            var formData = new FormData();
            formData.append('files', file);
            formData.append('code', 'AI-IMPORT-' + Date.now());
            var uploadUrl = '/' + aiJobQueueAPI.routeURL + '/uploadImportFile?job_type=' + encodeURIComponent(aiJobType);
            aiJobQueueAPI.upload(uploadUrl, formData, function (uploadResult) {
                if (uploadResult && uploadResult.error) {
                    reject(new Error(typeof uploadResult.error === 'string'
                        ? uploadResult.error
                        : uploadResult.message || 'Upload failed'));
                    return;
                }
                var uploadData = (uploadResult && uploadResult.data) || uploadResult || {};
                var fileUrl = null;
                var fileCode = null;
                if (uploadData.rows && uploadData.rows.length > 0) {
                    fileUrl = uploadData.rows[0].full_path || uploadData.rows[0].file_url || uploadData.rows[0].url;
                    fileCode = uploadData.rows[0].code;
                } else if (uploadData.full_path) {
                    fileUrl = uploadData.full_path;
                } else if (uploadData.url) {
                    fileUrl = uploadData.url;
                }
                if (!fileUrl && !fileCode) {
                    reject(new Error('Upload không trả về URL/code'));
                    return;
                }
                resolve({ file_url: fileCode || fileUrl, file_full_url: fileUrl });
            });
        });
    };

    const processAI = async (fileData, hint) => {
        if (!aiJobQueueAPI || !fileData || !fileData.file) {
            return { error: 'Thiếu API hoặc file' };
        }
        var file = fileData.file;
        var fileSizeMB = (file.size || 0) / 1024 / 1024;
        var syncMode = fileSizeMB <= aiSyncThresholdMB && fileData.kind !== 'pdf';

        try {
            // Step 1: Upload
            forceRenderWithState({
                aiProcessing: true,
                aiProgressStep: 0,
                aiProgressText: AntLocalization.translateText({ vi: 'Đang tải file lên...', en: 'Uploading file...', fr: 'Envoi du fichier...', es: 'Subiendo archivo...', id: 'Mengunggah file...', ja: 'アップロード中...', zh: '正在上传文件...', ko: '파일 업로드 중...', hi: 'फ़ाइल अपलोड हो रही है...', ar: 'جارٍ رفع الملف...' }),
                aiError: '',
                aiAsyncJob: null
            });

            var uploaded = await uploadFileToMedia(file);

            // Step 2: Extract (AI bóc tách)
            forceRenderWithState({
                aiProgressStep: 1,
                aiProgressText: AntLocalization.translateText({ vi: 'AI đang trích xuất dữ liệu...', en: 'AI extracting data...', fr: 'L\'IA extrait les données...', es: 'La IA extrae los datos...', id: 'AI mengekstrak data...', ja: 'AIがデータを抽出中...', zh: 'AI正在提取数据...', ko: 'AI가 데이터 추출 중...', hi: 'AI डेटा निकाल रहा है...', ar: 'AI يستخرج البيانات...' })
            });

            var inputData = { auto_apply: false };
            if (aiEntity) inputData.entity = aiEntity;
            if (aiVariant) inputData.variant = aiVariant;
            if (aiExtraInputData && typeof aiExtraInputData === 'object') Object.assign(inputData, aiExtraInputData);

            var result = await aiJobQueueAPI.postActionWithData('actionEnqueue', {
                job_type: aiJobType,
                file_url: uploaded.file_url,
                file_name: file.name,
                file_mime: file.type,
                file_size: file.size,
                source_channel: 'AntImport',
                source_target_table: aiTargetTable,
                source_extra_hint: hint || null,
                sync_mode: syncMode,
                input_data: inputData
            });

            var data = (result && result.data) || result || {};
            if (data.error) {
                forceRenderWithState({
                    aiProcessing: false,
                    aiError: data.message || AntLocalization.translateText({ vi: 'AI xử lý lỗi', en: 'AI error', fr: 'Erreur IA', es: 'Error de IA', id: 'AI gagal', ja: 'AI処理エラー', zh: 'AI处理失败', ko: 'AI 처리 실패', hi: 'AI त्रुटि', ar: 'خطأ في AI' })
                });
                return { error: data.message };
            }

            if (props.onAIJobCreated && data.job) props.onAIJobCreated(data.job.code);

            // Async mode: hiển thị banner riêng + nút mở hàng đợi AI (không trộn vào aiError)
            if (!data.sync_mode || !data.job || data.job.job_status === 'Pending' || data.job.job_status === 'Processing') {
                forceRenderWithState({
                    aiProcessing: false,
                    aiJob: data.job,
                    aiAsyncJob: data.job || { code: '', file_name: file.name }
                });
                loadRecentJobs();
                return { async: true };
            }

            // Sync done → extract rows from output_data
            var job = data.job;
            var outputData = job.output_data;
            if (typeof outputData === 'string') {
                try { outputData = JSON.parse(outputData); } catch (e) { outputData = {}; }
            }
            outputData = outputData || {};
            var rows = outputData.rows || [];

            // Analysis-style output (extract_voucher / extract_contract / ...): worker
            // tạo 1 record review riêng (vd AIVoucherAnalysis) thay vì rows-style.
            // Đóng popup AntImport + chuyển user sang trang chi tiết AIJobQueue để
            // review/apply. Detail page có button mở record analysis và tạo voucher.
            var analysisCode = outputData.analysis_code || null;
            var appliedTargetCode = outputData.applied && outputData.target_code ? outputData.target_code : null;
            if (!rows.length && (analysisCode || appliedTargetCode)) {
                forceRenderWithState({
                    aiProcessing: false,
                    aiJob: job
                });
                loadRecentJobs();
                if (popup.current) popup.current.close();
                if (props.onAIAnalysisDone) {
                    props.onAIAnalysisDone({
                        job: job,
                        analysis_code: analysisCode,
                        target_code: appliedTargetCode,
                        target_table: outputData.target_table || aiTargetTable
                    });
                } else {
                    try {
                        var moduleName = (props.module || (props.API && props.API.moduleName) || '').toLowerCase();
                        var targetUrl = appliedTargetCode && moduleName
                            ? '/' + moduleName + '/' + (outputData.target_table || aiTargetTable) + '/' + encodeURIComponent(appliedTargetCode)
                            : '/ai/AIJobQueue/' + encodeURIComponent(job.code || job.id);
                        navigate(targetUrl);
                    } catch (e) { /* ignore */ }
                }
                return { analysis: true, analysis_code: analysisCode, target_code: appliedTargetCode };
            }

            if (!rows.length) {
                forceRenderWithState({
                    aiProcessing: false,
                    aiError: AntLocalization.translateText({ vi: 'AI không trích xuất được dòng nào', en: 'AI returned no rows', fr: 'L\'IA n\'a extrait aucune ligne', es: 'La IA no extrajo ninguna fila', id: 'AI tidak mengekstrak baris apa pun', ja: 'AIは行を抽出できませんでした', zh: 'AI未提取到任何行', ko: 'AI가 추출한 행이 없습니다', hi: 'AI कोई पंक्ति नहीं निकाल सका', ar: 'لم يستخرج AI أي صف' })
                });
                loadRecentJobs();
                return { error: 'no rows' };
            }

            // Convert AI rows to {headers, rows} for existing AntImportFieldMapper
            var converted = convertAIRowsToFileData(rows);
            var aiSuggestedMapping = Array.isArray(outputData.suggested_mapping) ? outputData.suggested_mapping : null;
            var aiColumnLabels = (outputData.column_labels && typeof outputData.column_labels === 'object') ? outputData.column_labels : null;

            forceRenderWithState({
                aiProcessing: false,
                aiJob: job,
                fileData: {
                    name: file.name,
                    kind: fileData.kind,
                    headers: converted.headers,
                    rows: converted.rows,
                    aiMeta: converted.aiMeta,
                    aiSuggestedMapping: aiSuggestedMapping,
                    aiColumnLabels: aiColumnLabels,
                    aiJobCode: job.code,
                    isDate1904: false
                }
            });
            loadRecentJobs();
            return { ok: true, headers: converted.headers, rows: converted.rows };
        } catch (e) {
            forceRenderWithState({
                aiProcessing: false,
                aiError: e.message || String(e)
            });
            return { error: e.message || String(e) };
        }
    };

    const convertAIRowsToFileData = (aiRows) => {
        // AIMasterAnalysis trả về rows = [{ name, uom, description, brand, barcode, catalogue, price, vat_pct, _ai_confidence?, _ai_issues?, _duplicate_group? }, ...]
        // Underscore-prefixed fields = AI metadata (confidence, issues, duplicate). Extract into parallel aiMeta array.
        var headerSet = {};
        aiRows.forEach((r) => {
            if (r && typeof r === 'object') {
                Object.keys(r).forEach((k) => {
                    if (k.charAt(0) === '_') return; // skip _ai_* metadata
                    headerSet[k] = true;
                });
            }
        });
        var headers = Object.keys(headerSet);
        var rows = aiRows.map((r) => headers.map((h) => (r && r[h] !== undefined) ? r[h] : ''));
        var aiMeta = aiRows.map((r) => ({
            _ai_confidence: r && r._ai_confidence,
            _ai_issues: r && r._ai_issues,
            _duplicate_group: r && r._duplicate_group
        }));
        return { headers: headers, rows: rows, aiMeta: aiMeta };
    };

    const close = async () => {
        if (popup.current) popup.current.close();
    };

    const initWarningText = (states) => {
        if (states.currentStage !== 'FileBrowse') {
            if (!states.fileData || !states.fileData.name || !states.fileData.headers)
                return AntLocalization.translateText({ vi: 'Vui lòng chọn file !', en: 'Please select a file !', fr: 'Veuillez choisir un fichier !', es: '¡Seleccione un archivo!', id: 'Silakan pilih file!', ja: 'ファイルを選択してください！', zh: '请选择文件！', ko: '파일을 선택하세요!', hi: 'कृपया फ़ाइल चुनें!', ar: 'يرجى اختيار ملف!' });
        }

        if (states.fileData && states.fileData.rows && states.fileData.rows.length > 500) {
            return AntLocalization.translateText({ vi: 'Tối đa 500 dòng !', en: 'Maximum 500 lines !', fr: 'Maximum 500 lignes !', es: '¡Máximo 500 líneas!', id: 'Maksimal 500 baris!', ja: '最大500行！', zh: '最多500行！', ko: '최대 500행!', hi: 'अधिकतम 500 पंक्तियाँ!', ar: 'الحد الأقصى 500 سطر!' });
        }

        if (states.currentStage === 'ColumnMapping' && fieldMapper.current) {
            var result = fieldMapper.current.validate();

            var isValid = result.isValid;
            if (!isValid) return result.message;
        }
        if (states.currentStage !== 'FileBrowse' && (states.importMode === 'Update' || states.importMode === 'CreateUpdate')) {
            return AntLocalization.translateText({
                vi: 'Lưu ý: ô trống ở cột đã ánh xạ sẽ xóa giá trị cũ của dòng cập nhật; cột không ánh xạ sẽ được giữ nguyên.',
                en: 'Note: a blank cell in a mapped column clears the existing value on update rows; unmapped columns remain unchanged.',
                fr: 'Note : une cellule vide d\'une colonne mappée efface l\'ancienne valeur des lignes mises à jour ; les colonnes non mappées sont conservées.',
                es: 'Nota: una celda vacía en una columna mapeada borra el valor anterior de las filas actualizadas; las columnas sin mapear se conservan.',
                id: 'Catatan: sel kosong pada kolom yang dipetakan menghapus nilai lama baris yang diperbarui; kolom yang tidak dipetakan tetap dipertahankan.',
                ja: '注意: マッピング済みの列の空セルは更新行の既存値を消去します。未マッピングの列は変更されません。',
                zh: '注意：已映射列中的空单元格会清除更新行的原有值；未映射的列保持不变。',
                ko: '참고: 매핑된 열의 빈 셀은 업데이트 행의 기존 값을 지웁니다. 매핑되지 않은 열은 그대로 유지됩니다.',
                hi: 'ध्यान दें: मैप किए गए कॉलम का खाली सेल अपडेट पंक्ति का पुराना मान हटा देगा; बिना मैप वाले कॉलम जस के तस रहेंगे।',
                ar: 'ملاحظة: الخلية الفارغة في عمود مربوط تمسح القيمة القديمة للصف المحدَّث؛ الأعمدة غير المربوطة تبقى كما هي.'
            });
        }
        return '';
    };

    const initPopupWidgets = (states) => {
        if (states.currentStage === 'FileBrowse') {
            var widgets = [];
            var fd = states.fileData;
            var hasHeaders = fd && fd.headers && fd.headers.length;
            var isSpreadsheet = fd && fd.kind === 'spreadsheet';
            var needsAI = aiEnabled && fd && (fd.kind !== 'spreadsheet' || states.useAI);

            // AI luôn cần user bấm nút "Phân tích" trong AI panel (KHÔNG auto-trigger).
            // Btn "Tiếp theo" trong footer chỉ giữ cho:
            //  - AI tắt + spreadsheet đã parse headers → manual mapping
            //  - AI bật + spreadsheet + user TẮT useAI → manual mapping fallback
            // Non-spreadsheet KHÔNG có Btn "Tiếp theo" (chỉ có đường AI qua nút trong panel).
            if (fd && fd.file && !states.aiProcessing) {
                var showContinueManual = isSpreadsheet && hasHeaders && !needsAI;
                if (showContinueManual) {
                    widgets.push({
                        id: 'btnContinueManual',
                        type: 'button',
                        icon: 'spinright',
                        text: AntLocalization.translateText({ vi: 'Tiếp theo', en: 'Next', fr: 'Suivant', es: 'Siguiente', id: 'Berikutnya', ja: '次へ', zh: '下一步', ko: '다음', hi: 'अगला', ar: 'التालي' }),
                        waiting_text: AntLocalization.translateText({ vi: 'Chờ xử lý', en: 'Waiting', fr: 'En attente', es: 'Pendiente', id: 'Tertunda', ja: '保留中', zh: '待处理', ko: '보류 중', hi: 'लंबित', ar: 'معلق' }),
                        width: 150
                    });
                }
            }

            var mappings = props.mapping instanceof Array ? props.mapping : [];
            mappings = mappings.filter((item) => {
                return AntCondition.isVisible(props.parentData, item);
            });
            if (mappings instanceof Array && mappings.length > 0)
                widgets.push({
                    id: 'btnDownloadSample',
                    type: 'button',
                    icon: 'download',
                    text: AntLocalization.translateText({ vi: 'Tải file mẫu', en: 'Template', fr: 'Modèle', es: 'Plantilla', id: 'Template', ja: 'テンプレート', zh: '模板', ko: '템플릿', hi: 'टेम्पलेट', ar: 'قالب' }),
                    waiting_text: AntLocalization.translateText({ vi: 'Chờ xử lý', en: 'Waiting', fr: 'En attente', es: 'Esperando', id: 'Menunggu', ja: '待機中', zh: '等待中', ko: '대기 중', hi: 'प्रतीक्षा', ar: 'انتظار' }),
                    width: 150
                });
            return widgets;
        }
        if (states.currentStage === 'ColumnMapping') {
            var cmWidgets = [
                {
                    id: 'btnBack',
                    location: 'before',
                    type: 'button',
                    icon: 'back',
                    text: AntLocalization.translateText({ vi: 'Quay lại', en: 'Back', fr: 'Retour', es: 'Volver', id: 'Kembali', ja: '戻る', zh: '返回', ko: '뒤로', hi: 'वापस', ar: 'رجوع' }),
                    width: 120
                }
            ];
            if (fieldMapper.current) {
                var result = fieldMapper.current.validate();
                if (result && result.isValid)
                    cmWidgets.push({
                        id: 'btnPreview',
                        type: 'button',
                        icon: 'spinright',
                        text: AntLocalization.translateText({ vi: 'Xem trước', en: 'Preview', fr: 'Aperçu', es: 'Vista previa', id: 'Pratinjau', ja: 'プレビュー', zh: '预览', ko: '미리보기', hi: 'पूर्वावलोकन', ar: 'معاينة' }),
                        waiting_text: AntLocalization.translateText({ vi: 'Chờ xử lý', en: 'Waiting', fr: 'En attente', es: 'Esperando', id: 'Menunggu', ja: '待機中', zh: '等待中', ko: '대기 중', hi: 'प्रतीक्षा', ar: 'انتظार' }),
                        width: 150
                    });
            }
            return cmWidgets;
        }
        if (states.currentStage === 'Preview') {
            var ar = [
                {
                    id: 'btnBack',
                    location: 'before',
                    type: 'button',
                    icon: 'back',
                    text: AntLocalization.translateText({ vi: 'Quay lại', en: 'Back', fr: 'Retour', es: 'Volver', id: 'Kembali', ja: '戻る', zh: '返回', ko: '뒤로', hi: 'वापस', ar: 'رجوع' }),
                    width: 150
                }
            ];

            if (previewGrid.current) {
                var obj = previewGrid.current && previewGrid.current.validate();

                if (obj && !obj.hasError && obj.length > 0) {
                    ar = ar.concat([
                        {
                            id: 'btnImport',
                            type: 'button',
                            icon: 'parentfolder',
                            text: AntLocalization.translateText({ vi: 'Thực hiện', en: 'Execute', fr: 'Exécuter', es: 'Ejecutar', id: 'Jalankan', ja: '実行', zh: '执行', ko: '실행', hi: 'निष्पादित', ar: 'تنفيذ' }),
                            waiting_text: AntLocalization.translateText({ vi: 'Chờ xử lý', en: 'Waiting', fr: 'En attente', es: 'Esperando', id: 'Menunggu', ja: '待機中', zh: '等待中', ko: '대기 중', hi: 'प्रतीक्षा', ar: 'انتظار' }),
                            width: 150
                        }
                    ]);
                } else if (obj && obj.hasError && obj.length > 0)
                    ar = ar.concat([
                        {
                            id: 'btnRemoveErrorItems',
                            location: 'before',
                            type: 'button',
                            icon: 'remove',
                            text: AntLocalization.translateText({ vi: 'Bỏ dòng lỗi', en: 'Remove invalid rows', fr: 'Retirer lignes invalides', es: 'Quitar filas inválidas', id: 'Hapus baris tidak valid', ja: '無効な行を削除', zh: '删除无效记录', ko: '잘못된 레코드 제거', hi: 'अमान्य पंक्तियाँ हटाएँ', ar: 'حذف الصفوف الخاطئة' }),
                            waiting_text: AntLocalization.translateText({ vi: 'Chờ xử lý', en: 'Waiting', fr: 'En attente', es: 'Esperando', id: 'Menunggu', ja: '待機中', zh: '等待中', ko: '대기 중', hi: 'प्रतीक्षा', ar: 'انتظار' }),
                            width: 200
                        }
                    ]);
            }

            return ar;
        }
        return [];
    };

    const initPopupSize = (states) => {
        if (states.currentStage === 'FileBrowse') {
            // Khi AI bật → drop zone lớn + AIPreprocessorPanel cần thêm chiều cao
            if (aiEnabled) {
                return {
                    height: 600,
                    width: 640
                };
            }
            return {
                height: 200,
                width: 480
            };
        }
        if (states.currentStage === 'ColumnMapping') {
            return {
                height:
                    mainState.importFields.length > 0 && mainState.importFields.length * 34 + 226 < 600
                        ? mainState.importFields.length * 34 + 226
                        : 600,
                // props.heights && props.heights.length >= 2 ? props.heights[1] : 480
                width: 480
            };
        }
        if (states.currentStage === 'Preview') {
            let pageSize = props.defaultPageSize ? props.defaultPageSize : 8;
            return {
                height: pageSize * 34 + 290,
                width: props.previewWidth ? props.previewWidth : 950
            };
        }
        return {
            height: 200,
            width: 200
        };
    };

    const initImportFields = () => {
        var ar = [];

        var mappings = props.mapping instanceof Array ? props.mapping : [];
        mappings = mappings.filter((item) => {
            return AntCondition.isVisible(props.parentData, item);
        });

        for (var i = 0; i < mappings.length; i++) {
            let m = mappings[i];

            let label = m.label;
            let alignment = m.alignment;
            let required = m.required;
            let cnf = config.getCnfField(m.name, tableName);
            if (!cnf && (m.cnfTable || m.cnfField)) {
                let cnfTableName = m.cnfTable ? m.cnfTable : tableName;
                let cnfFieldName = m.cnfField ? m.cnfField : m.name;
                cnf = config.getCnfField(cnfFieldName, cnfTableName);
            }

            if (cnf) {
                if (cnf.visibleOnMain && props.parentData) {
                    var visible = true;
                    let vObj = cnf.visibleOnMain;
                    if (cnf.visibleOnMain instanceof Array) {
                        vObj = cnf.visibleOnMain.find(
                            (v) =>
                                v.table === tableName ||
                                (v.table instanceof Array && v.table.includes(tableName)) ||
                                (v.tables instanceof Array && v.tables.includes(tableName))
                        );

                        if (vObj) vObj = vObj.value;
                    }
                    if (vObj) {
                        visible = AntCondition.isVisible(props.parentData, {
                            visible: vObj
                        });
                    }
                    if (!visible) continue;
                }

                if (!label) {
                    if (cnf.gridLabel) label = cnf.gridLabel;
                    else label = cnf.label;
                }

                if (!alignment && cnf.type == 'bool') alignment = 'center';

                if (required === undefined && cnf.clientRules instanceof Array) required = cnf.clientRules.some((r) => r.type === 'required');
            }
            // Chỉ field có metadata thật mới được hiểu là có thể clear khi ô đã map bị để trống.
            // Các cột import trung gian (item_name, warehouse_name...) được hook dùng để tra cứu,
            // không phải cột lưu DB và không được tự biến thành một lệnh SET NULL.
            let clearEmptyField = resolveImportClearField(m, cnf);
            ar.push({
                ...m,
                label: AntLocalization.translateText(AntCondition.getValue({}, label)),
                dataField: m.name,
                alignment: alignment,
                required: required,
                clearEmptyField: clearEmptyField
            });
        }
        return ar;
    };

    const onSaving = (row) => {
        if (props.parentFK && Object.keys(props.parentFK).length > 0) {
            row = Object.assign(row, props.parentFK);
        }

        if (props.parentData && Object.keys(props.parentData).length > 0 && props.copyFromParentFields instanceof Array) {
            props.copyFromParentFields.forEach((field) => {
                if (typeof field === 'object') {
                    var parentField = field.field;
                    var aliasField = field.alias ? field.alias : field.field;

                    if (props.parentData && props.parentData[parentField] !== undefined && props.parentData[parentField] !== null) {
                        if (field.when && typeof field.when === 'object') {
                            var isValid = AntCondition.isValid(props.parentData, {
                                when: field.when,
                                whenType: field.whenType
                            });
                            if (!isValid) return;
                        }
                        row[aliasField] = props.parentData[parentField];
                    }
                } else if (props.parentData && props.parentData[field] !== undefined && props.parentData[field] !== null)
                    row[field] = props.parentData[field];
            });
        }

        Object.keys(row).forEach((k) => {
            if (
                row[k] instanceof Date ||
                (typeof row[k] === 'string' && fixImportedDate(row[k], mainState.fileData && mainState.fileData.isDate1904 ? true : false))
            ) {
                row[k] = fixImportedDate(row[k], mainState.fileData && mainState.fileData.isDate1904 ? true : false);
            }

            if (row[k] instanceof Date) {
                row[k] = AntTime.init(row[k]).unix();
            }
        });

        return row;
    };

    const onAction = async (button) => {
        if (button && button.id == 'btnAIExtract') {
            var fd = mainState.fileData;
            if (!fd || !fd.file) return false;
            await processAI(fd, mainState.aiHint);
            return false;
        }

        if (button && button.id == 'btnContinueManual') {
            forceRenderWithState({ currentStage: 'ColumnMapping' });
            return false;
        }

        if (button && button.id == 'btnReopenJob') {
            // Click recent job → fetch detail (output_data is getDetail-only) then reuse rows as fileData
            var jobSummary = button.payload || {};
            if (!aiJobQueueAPI || !jobSummary.id) {
                forceRenderWithState({ aiError: AntLocalization.translateText({ vi: 'Không tìm thấy phiên AI', en: 'AI job not found', fr: 'Tâche IA introuvable', es: 'Tarea de IA no encontrada', id: 'Tugas AI tidak ditemukan', ja: 'AIジョブが見つかりません', zh: '未找到AI任务', ko: 'AI 작업을 찾을 수 없습니다', hi: 'AI जॉब नहीं मिला', ar: 'لم يُعثر على مهمة AI' }) });
                return false;
            }
            forceRenderWithState({
                aiProcessing: true,
                aiProgressStep: 1,
                aiProgressText: AntLocalization.translateText({ vi: 'Đang tải kết quả AI...', en: 'Loading AI result...', fr: 'Chargement résultat IA...', es: 'Cargando resultado de IA...', id: 'Memuat hasil AI...', ja: 'AI結果を読み込み中...', zh: '正在加载AI结果...', ko: 'AI 결과 로딩 중...', hi: 'AI परिणाम लोड हो रहा है...', ar: 'جارٍ تحميل نتيجة AI...' })
            });
            var detailResp = await aiJobQueueAPI.getDetail(jobSummary.id);
            var jobData = (detailResp && detailResp.data) || detailResp || jobSummary;
            var outputData = jobData.output_data;
            if (typeof outputData === 'string') {
                try { outputData = JSON.parse(outputData); } catch (e) { outputData = {}; }
            }
            outputData = outputData || {};
            var rows = outputData.rows || [];
            if (!rows.length) {
                forceRenderWithState({
                    aiProcessing: false,
                    aiError: AntLocalization.translateText({ vi: 'Phiên AI này không có dòng dữ liệu', en: 'This AI job has no rows', fr: 'Cette tâche IA n\'a aucune ligne', es: 'Esta tarea de IA no tiene filas', id: 'Tugas AI ini tidak memiliki baris', ja: 'このAIジョブには行がありません', zh: '此AI任务没有数据行', ko: '이 AI 작업에는 행이 없습니다', hi: 'इस AI जॉब में कोई पंक्ति नहीं', ar: 'مهمة AI هذه بلا صفوف' })
                });
                return false;
            }
            var converted = convertAIRowsToFileData(rows);
            var aiSuggestedMapping = Array.isArray(outputData.suggested_mapping) ? outputData.suggested_mapping : null;
            var aiColumnLabels = (outputData.column_labels && typeof outputData.column_labels === 'object') ? outputData.column_labels : null;
            forceRenderWithState({
                aiProcessing: false,
                fileData: {
                    name: jobData.file_name || jobData.code,
                    kind: 'ai-reopen',
                    headers: converted.headers,
                    rows: converted.rows,
                    aiMeta: converted.aiMeta,
                    aiSuggestedMapping: aiSuggestedMapping,
                    aiColumnLabels: aiColumnLabels,
                    aiJobCode: jobData.code,
                    isDate1904: false
                },
                aiJob: jobData
            });
            return false;
        }

        if (button && button.id == 'btnPreview') {
            forceRenderWithState({ currentStage: 'Preview' });
            return false;
        }

        if (button && button.id == 'btnImport') {
            if (previewGrid.current) {
                var data = previewGrid.current.getDataRows();
                if (data && data instanceof Array && mainState.defaultObj)
                    data = data.map((row) => {
                        return onSaving({
                            ...row,
                            ...mainState.defaultObj
                        });
                    });

                if (data && data instanceof Array && props.onPostingCustomData) data = props.onPostingCustomData(data);

                if (props.onImport) {
                    forceRenderWithState({
                        importing: true
                    });
                    await props.onImport(data);

                    if (props.onImported) props.onImported();
                    forceRenderWithState({
                        importing: false
                    });
                    return true;
                }

                if (props.onImporting) data = props.onImporting(data);

                var ars = [];
                while (data.length) {
                    ars.push(data.splice(0, 100));
                }

                forceRenderWithState({
                    importing: true
                });
                var newRows = await Promise.allSettled(
                    ars.map((ar) => {
                        return new Promise(async (done) => {
                            var result = await props.API.postImport(ar, mainState.importMode);
                            var isValid = result && !result.error;
                            done(isValid);
                        });
                    })
                );
                newRows = newRows.filter((i) => i.status === 'fulfilled').map((i) => i.value);
                newRows = newRows.flat();
                var isValid = newRows.filter((r) => Boolean(r)).length > 0;

                if (isValid && props.onImported) props.onImported();

                forceRenderWithState({
                    importing: false
                });
            }
        }

        if (button && button.id == 'btnBack') {
            if (mainState.currentStage === 'Preview') forceRenderWithState({ currentStage: 'ColumnMapping' });
            else if (mainState.currentStage === 'ColumnMapping')
                forceRenderWithState({
                    currentStage: 'FileBrowse',
                    fileData: null,
                    aiJob: null,
                    aiAsyncJob: null,
                    aiProcessing: false,
                    aiError: ''
                });

            return false;
        }

        if (button && button.id == 'btnRemoveErrorItems') {
            if (previewGrid.current) {
                var newRowNum = previewGrid.current.removeAllErrorItems();
                if (newRowNum <= 0) {
                    forceRenderWithState({ currentStage: 'FileBrowse' });
                }
            }
            return false;
        }

        if (button && button.id == 'btnDownloadSample') {
            var mFields = [];

            var mappings = props.mapping instanceof Array ? props.mapping : [];
            mappings = mappings.filter((item) => {
                return AntCondition.isVisible(props.parentData, item);
            });

            for (var i = 0; i < mappings.length; i++) {
                var m = mappings[i];

                var label = m.label;
                var found = config.getCnfField(m.name, tableName);

                if (found) {
                    if (!label) {
                        if (found.gridLabel) label = found.gridLabel;
                        else label = found.label;
                    }
                }

                if (!label) {
                    var cnfTableName = m.cnfTable ? m.cnfTable : tableName;
                    var cnfFieldName = m.cnfField ? m.cnfField : m.name;
                    var found = config.getCnfField(cnfFieldName, cnfTableName);
                    if (found) label = found.label;
                }

                var mRequired = m.required;
                if (mRequired === undefined && found && found.clientRules instanceof Array)
                    mRequired = found.clientRules.some((r) => r.type === 'required');

                mFields.push({
                    field: m.name,
                    alias: AntLocalization.translateText(AntCondition.getValue({}, label)),
                    required: Boolean(mRequired),
                    update: m.update !== false
                });
            }

            if (mFields.length > 0)
                var result = await props.API.downloadImportSample({
                    selectFields: mFields
                });

            return false;
        }

        return true;
    };

    const popup = useRef();
    const fileChooser = useRef();
    const fieldMapper = useRef();
    const previewGrid = useRef();

    const [mainState, setMainState] = useState(() => {
        let obj = {
            currentStage: 'FileBrowse',
            importMode: isUpdateOnly ? 'Update' : isCreatable ? 'Create' : '',
            fileData: null,
            importFields: initImportFields(),
            mappingFields: [],
            mappedFields: [],
            defaultObj: {},
            importing: false,
            // AI state
            useAI: false,
            aiHint: '',
            aiAvailable: null,
            recentJobs: [],
            aiProcessing: false,
            aiProgressText: '',
            aiError: '',
            aiJob: null
        };
        return { ...obj, warningText: initWarningText(obj), popupWidgets: initPopupWidgets(obj), popupSize: initPopupSize(obj) };
    });

    const forceRender = () => {
        setShouldRender((v) => v + 1);
    };

    const forceRenderWithState = (data) => {
        setMainState((v) => onCustomMainState(Object.assign(v, data), data));
        setShouldRender((v) => v + 1);
    };

    const onCustomMainState = (data, changeData) => {
        data.modeChangable =
            data.currentStage !== 'Preview' && !isCreateOnly && !isUpdateOnly && props.changeMode !== undefined ? props.changeMode : true;

        if (changeData.fileData) {
            // aiEnabled=false → legacy auto-transition (spreadsheet with headers parsed locally).
            // aiEnabled=true  → always stay at FileBrowse so user clicks the explicit Next button.
            // AI-completed result (aiJobCode set) → transition.
            var fd = data.fileData;
            var hasHeaders = fd && fd.headers && fd.headers.length;
            var fromAI = fd && fd.aiJobCode;
            var isSpreadsheet = fd && fd.kind === 'spreadsheet';
            if (fromAI) {
                data.currentStage = 'ColumnMapping';
            } else if (!aiEnabled && isSpreadsheet && hasHeaders) {
                data.currentStage = 'ColumnMapping';
            }
        }
        if (changeData.currentStage === 'Preview')
            data.mappedFields = fieldMapper.current ? fieldMapper.current.getMappedFields() : data.mappedFields;

        // data.importFields = initImportFields();
        data.warningText = initWarningText(data);

        if (changeData.currentStage || changeData.fileData) data.popupSize = initPopupSize(data);

        data.popupWidgets = initPopupWidgets(data);
        return data;
    };

    const [shouldRender, setShouldRender] = useState(1);

    useImperativeHandle(ref, () => ({
        show: show,
        close: close
    }));

    return (
        <React.Fragment>
            {shouldRender && (
                <AntPopup
                    id={'ant-import'}
                    shouldRender={shouldRender + (props.shouldRender ? props.shouldRender : 1)}
                    ref={popup}
                    isGray={true}
                    deferRendering={true}
                    width={mainState.popupSize.width}
                    height={mainState.popupSize.height}
                    // autoHeight={true}
                    // autoWidth={true}
                    title={`${title} ${String(entityDisplay).toLowerCase()}`}
                    onAction={onAction}
                    closeWidth={120}
                    widgets={mainState.popupWidgets}
                >
                    {/* Khi AI đang phân tích — chiếm full popup body, ẩn FileBrowser
                        + AI panel. Inline style (không phụ thuộc SCSS rebuild). */}
                    {mainState.aiProcessing && <AntImportAIProcessing fileData={mainState.fileData} progressText={mainState.aiProgressText} />}

                    {mainState.currentStage === 'FileBrowse' && !mainState.aiProcessing && (
                        <AntImportFileBrowser
                            ref={fileChooser}
                            API={props.API}
                            aiEnabled={aiEnabled}
                            importMode={mainState.importMode}
                            modeChangable={mainState.modeChangable}
                            createable={Boolean(isCreatable)}
                            updateable={Boolean(isUpdatable)}
                            isCreateOnly={isCreateOnly}
                            isUpdateOnly={isUpdateOnly}
                            onChangeMode={(mode) => forceRenderWithState({ importMode: mode })}
                            onChoosed={(data) => {
                                // Chỉ set fileData — không auto-chạy AI.
                                // User phải bấm nút "Phân tích" trong AI panel để trigger processAI.
                                forceRenderWithState({ fileData: data });
                            }}
                        />
                    )}

                    {mainState.currentStage === 'FileBrowse' && aiEnabled && !mainState.aiProcessing && (
                        <AIPreprocessorPanel
                            state={mainState}
                            aiAvailable={mainState.aiAvailable}
                            onToggleAI={(v) => forceRenderWithState({ useAI: v })}
                            onChangeHint={(v) => forceRenderWithState({ aiHint: v })}
                            onRunAI={() => {
                                if (mainState.fileData && mainState.fileData.file) {
                                    processAI(mainState.fileData, mainState.aiHint);
                                }
                            }}
                            onReopenJob={(job) => onAction({ id: 'btnReopenJob', payload: job })}
                            onClearError={() => forceRenderWithState({ aiError: '' })}
                            onDismissAsync={() => forceRenderWithState({ aiAsyncJob: null })}
                            onOpenQueue={() => {
                                if (popup.current) popup.current.close();
                                try { navigate('/ai/AIJobQueues'); } catch (e) { /* noop */ }
                            }}
                        />
                    )}

                    <AntImportFieldMapper
                        ref={fieldMapper}
                        API={props.API}
                        importFields={mainState.importFields}
                        importMode={mainState.importMode}
                        fileColumns={mainState.fileData && mainState.fileData.headers ? mainState.fileData.headers : []}
                        aiSuggestedMapping={mainState.fileData && mainState.fileData.aiSuggestedMapping ? mainState.fileData.aiSuggestedMapping : null}
                        aiColumnLabels={mainState.fileData && mainState.fileData.aiColumnLabels ? mainState.fileData.aiColumnLabels : null}
                        onMapping={(fields) => forceRenderWithState({ mappingFields: fields })}
                        onMapped={(fields) => forceRenderWithState({ mappedFields: fields })}
                        visible={mainState.currentStage === 'ColumnMapping'}
                    />
                    <AntImportPreviewGrid
                        ref={previewGrid}
                        API={props.API}
                        defaultPageSize={props.defaultPageSize}
                        importFields={mainState.importFields}
                        importMode={mainState.importMode}
                        fileRows={mainState.fileData && mainState.fileData.rows ? mainState.fileData.rows : []}
                        aiMeta={mainState.fileData && mainState.fileData.aiMeta ? mainState.fileData.aiMeta : null}
                        aiColumnLabels={mainState.fileData && mainState.fileData.aiColumnLabels ? mainState.fileData.aiColumnLabels : null}
                        isDate1904={mainState.fileData && mainState.fileData.isDate1904 ? true : false}
                        mappedFields={mainState.mappedFields}
                        defaultObj={mainState.defaultObj}
                        onLoaded={(data) => forceRenderWithState({ gridData: data })}
                        visible={mainState.currentStage === 'Preview'}
                        editable={!Boolean(mainState.importing)}
                        onValidateCustomData={props.onValidateCustomData}
                    />

                    {mainState.warningText && <h6 className="ant-footer-text ant-warning-text">{mainState.warningText}</h6>}
                </AntPopup>
            )}
        </React.Fragment>
    );
});
